import { defineStore } from 'pinia'
import { ref } from 'vue'
import { Capacitor } from '@capacitor/core'
import { ApiError } from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import {
  openPhoneHealthSettings,
  phoneHealthAvailable,
  phoneHealthConnect,
  phoneHealthDisconnect,
  phoneHealthMissing,
  phoneHealthState,
  phoneHealthSync,
  type PhoneHealthSummary,
} from '@/native/health'
import { mealsOutOn, turnMealsOutOff, turnMealsOutOn, writeMealsOut } from '@/native/meals-out'

/** Do not read the store again within this long of the last time. */
const AUTO_SYNC_GAP_MS = 15 * 60_000
/** After a meal changes, wait this long for more before writing out. */
const MEALS_CHANGED_WAIT_MS = 3_000

/**
 * This phone's health store, for the person signed in on it (M-72). One
 * state for the whole app: the Connections card shows it, the app shell
 * syncs on coming to the foreground.
 */
export const usePhoneHealthStore = defineStore('phoneHealth', () => {
  const native = ref(Capacitor.isNativePlatform())
  const checked = ref(false)
  const available = ref(false)
  const reason = ref<string | null>(null)
  const connected = ref(false)
  const last = ref<PhoneHealthSummary | null>(null)
  const busy = ref(false)
  const error = ref<string | null>(null)
  /** Meals eaten are written out to Health Connect (S-70, ADR-037). */
  const mealsOut = ref(false)
  const mealsOutBusy = ref(false)
  const mealsOutError = ref<string | null>(null)
  let mealsOutAt = 0

  function fail(err: unknown, fallback: string) {
    error.value = err instanceof ApiError ? err.message : ((err as Error)?.message ?? fallback)
  }

  async function init() {
    if (!native.value) {
      checked.value = true
      return
    }
    const a = await phoneHealthAvailable()
    available.value = a.available
    reason.value = a.reason
    if (a.available) {
      const state = await phoneHealthState()
      connected.value = state.connected
      last.value = state.last
      mealsOut.value = await mealsOutOn()
    }
    checked.value = true
    await autoSync()
  }

  function myMemberId(): string | null {
    return useAuthStore().me?.memberId ?? null
  }

  async function connect() {
    const memberId = myMemberId()
    if (!memberId) {
      error.value = 'This login is not a member of the household yet.'
      return
    }
    busy.value = true
    error.value = null
    try {
      const { granted } = await phoneHealthConnect()
      connected.value = granted.length > 0
      if (!connected.value) {
        error.value = 'Nothing was allowed. Allow at least one kind of data in Health Connect.'
        return
      }
      last.value = await phoneHealthSync(memberId)
    } catch (err) {
      fail(err, 'Could not read this phone’s health data')
    } finally {
      busy.value = false
    }
  }

  /**
   * A sync somebody asked for.
   *
   * It asks for anything the app now wants and has never been granted,
   * before reading. Connecting is the only thing that has ever requested
   * permissions, so a data type added in a later release was never asked
   * for by anybody already connected: Health Connect refused the read, the
   * sync swallowed it so one refusal could not cost somebody their steps,
   * and the data simply never appeared. That is how heart rate shipped
   * unreadable.
   *
   * Only here, never on the automatic sync. A prompt is reasonable when
   * somebody has just pressed a button and is watching; arriving unbidden
   * when the app came to the foreground is not, and Health Connect stops
   * honouring repeated requests anyway.
   */
  async function syncNow() {
    const memberId = myMemberId()
    if (!memberId || !connected.value) return
    busy.value = true
    error.value = null
    try {
      if ((await phoneHealthMissing()).length > 0) await phoneHealthConnect()
      last.value = await phoneHealthSync(memberId)
    } catch (err) {
      fail(err, 'The sync did not finish')
    } finally {
      busy.value = false
    }
  }

  /** On coming to the foreground: quietly, and not more than every quarter hour. */
  async function autoSync() {
    if (mealsOut.value && Date.now() - mealsOutAt >= AUTO_SYNC_GAP_MS) void writeMeals()
    if (!connected.value || busy.value) return
    const lastAt = last.value ? Date.parse(last.value.at) : 0
    if (Date.now() - lastAt < AUTO_SYNC_GAP_MS) return
    await syncNow()
  }

  /** Turn writing meals out on: Health Connect asks for permission, then the week is written. */
  async function startMealsOut() {
    mealsOutError.value = null
    try {
      mealsOut.value = await turnMealsOutOn()
      if (!mealsOut.value) {
        mealsOutError.value = 'Health Connect did not allow it. Allow Refuelled to write nutrition, then try again.'
        return
      }
      await writeMeals()
    } catch (err) {
      mealsOutError.value = (err as Error)?.message ?? 'Could not ask Health Connect'
    }
  }

  async function stopMealsOut() {
    mealsOut.value = false
    mealsOutError.value = null
    await turnMealsOutOff()
  }

  /** Put Health Connect right for the last week. Quiet on failure apart from the message. */
  async function writeMeals() {
    const memberId = myMemberId()
    if (!mealsOut.value || !memberId || mealsOutBusy.value) return
    mealsOutBusy.value = true
    try {
      await writeMealsOut(memberId)
      mealsOutAt = Date.now()
      mealsOutError.value = null
    } catch (err) {
      mealsOutError.value = (err as Error)?.message ?? 'Could not write to Health Connect'
    } finally {
      mealsOutBusy.value = false
    }
  }

  /**
   * A meal was logged, corrected or deleted on this phone. Several changes
   * in a row (a whole meal entered food by food) are written once.
   */
  let mealsTimer: ReturnType<typeof setTimeout> | null = null
  function mealsChanged() {
    if (!mealsOut.value) return
    if (mealsTimer) clearTimeout(mealsTimer)
    mealsTimer = setTimeout(() => {
      mealsTimer = null
      void writeMeals()
    }, MEALS_CHANGED_WAIT_MS)
  }

  async function disconnect() {
    busy.value = true
    try {
      await phoneHealthDisconnect()
      connected.value = false
      last.value = null
    } finally {
      busy.value = false
    }
  }

  async function openSettings() {
    try {
      await openPhoneHealthSettings()
    } catch (err) {
      fail(err, 'Could not open Health Connect')
    }
  }

  return {
    native,
    checked,
    available,
    reason,
    connected,
    last,
    busy,
    error,
    mealsOut,
    mealsOutBusy,
    mealsOutError,
    init,
    connect,
    syncNow,
    autoSync,
    disconnect,
    openSettings,
    startMealsOut,
    stopMealsOut,
    writeMeals,
    mealsChanged,
  }
})
